import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Star } from "lucide-react";

const BODY = ["muscular", "athletic", "slim", "skinny"];
const ROLE = ["top", "versatile", "bottom"];
const CAT = ["popular", "new"];

export default function Therapists() {
  const [list, setList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({ branch_id: "", body_type: "", role_preference: "", category: "" });

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    api.get(`/therapists?${params.toString()}`).then((r) => setList(r.data)).catch(() => {});
  }, [filters]);

  return (
    <div data-testid="therapists-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <div className="label-eyebrow mb-4">Meet the Team</div>
      <h1 className="text-5xl lg:text-6xl font-heading text-white mb-6">Therapists</h1>
      <p className="text-hsecondary max-w-2xl">Handpicked professionals, extensively trained. Every therapist listed is available today unless marked otherwise.</p>

      <div className="border border-white/10 p-6 mt-10 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <div className="label-eyebrow mb-2">Branch</div>
          <select data-testid="filter-branch" value={filters.branch_id} onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 text-sm text-white outline-none">
            <option value="">All</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <div className="label-eyebrow mb-2">Body</div>
          <select data-testid="filter-body" value={filters.body_type} onChange={(e) => setFilters({ ...filters, body_type: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 text-sm text-white outline-none">
            <option value="">All</option>
            {BODY.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <div className="label-eyebrow mb-2">Role</div>
          <select data-testid="filter-role" value={filters.role_preference} onChange={(e) => setFilters({ ...filters, role_preference: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 text-sm text-white outline-none">
            <option value="">All</option>
            {ROLE.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <div className="label-eyebrow mb-2">Category</div>
          <select data-testid="filter-category" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 text-sm text-white outline-none">
            <option value="">All</option>
            {CAT.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button onClick={() => setFilters({ branch_id: "", body_type: "", role_preference: "", category: "" })} data-testid="filter-clear" className="ghost-btn justify-center self-end">Clear</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-8">
        {list.map((t) => {
          const branch = branches.find((b) => b.id === t.branch_id);
          return (
            <div key={t.id} data-testid={`therapist-card-${t.display_name}`} className="group">
              <div className="aspect-[3/4] overflow-hidden bg-hpaper relative mb-4">
                <img src={t.photos?.[0]} alt={t.display_name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute top-3 left-3 label-eyebrow bg-black/70 backdrop-blur-sm px-2 py-1">{t.body_type}</div>
                {t.category?.includes("popular") && (
                  <div className="absolute top-3 right-3 label-eyebrow bg-hgold text-black px-2 py-1">Popular</div>
                )}
                {t.category?.includes("new") && !t.category?.includes("popular") && (
                  <div className="absolute top-3 right-3 label-eyebrow bg-white/80 text-black px-2 py-1">New</div>
                )}
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <Link to={`/book?therapist=${t.id}&branch=${t.branch_id}`} className="gold-btn w-full justify-center" style={{padding:"0.5rem 1rem", fontSize:"0.7rem"}}>Book Now</Link>
                </div>
              </div>
              <h3 className="font-heading text-xl text-white">{t.display_name}</h3>
              <div className="flex items-center gap-2 mt-1 text-xs">
                <span className="text-hgold flex items-center gap-1"><Star size={11} fill="currentColor"/>{t.rating?.toFixed(1)}</span>
                <span className="text-white/40">·</span>
                <span className="text-white/50">{t.experience_years}y</span>
                <span className="text-white/40">·</span>
                <span className="text-white/50 uppercase tracking-widest">{t.role_preference}</span>
              </div>
              {branch && <div className="text-xs text-white/40 mt-1">{branch.name}</div>}
            </div>
          );
        })}
        {list.length === 0 && <div className="col-span-full text-hsecondary text-center py-12">No therapists match your filters.</div>}
      </div>
    </div>
  );
}
